// FORGED Ed Systems — DEPLOY003 — v0.3.0 — IntakeStep3Program.tsx
// Last touched: 2026-05-19
//
// Step 3 of intake: Your program — teaching background + equipment + calendar.

import type {
  InstructorBlock,
  EquipmentBlock,
  CalendarBlock,
  InstructorBackground,
  MachineEntry,
  WeldProcessType,
} from "./intakeTypes";

type Props = {
  instructor: InstructorBlock;
  equipment: EquipmentBlock;
  calendar: CalendarBlock;
  onChangeInstructor: (block: InstructorBlock) => void;
  onChangeEquipment: (block: EquipmentBlock) => void;
  onChangeCalendar: (block: CalendarBlock) => void;
};

const BACKGROUND_OPTIONS: { id: InstructorBackground; label: string }[] = [
  { id: "welding_only", label: "Welding teacher — this is my primary discipline" },
  { id: "ag_mech_assigned", label: "Ag-Mech teacher — welding is one of my courses" },
  { id: "industrial_arts", label: "Industrial Arts teacher — welding is one of several courses" },
  { id: "other_cte_assigned", label: "Other CTE teacher — assigned to teach welding" },
  { id: "new_to_cte", label: "New to CTE teaching (under 2 years total)" },
  { id: "other", label: "Other (we'll ask for context)" },
];

const PROCESS_OPTIONS: WeldProcessType[] = ["SMAW", "GMAW", "GTAW", "FCAW", "SAW"];

const CROSS_TRAINED: InstructorBackground[] = [
  "ag_mech_assigned",
  "industrial_arts",
  "other_cte_assigned",
  "new_to_cte",
];

export default function IntakeStep3Program({
  instructor,
  equipment,
  calendar,
  onChangeInstructor,
  onChangeEquipment,
  onChangeCalendar,
}: Props) {
  function updateInstructor<K extends keyof InstructorBlock>(
    key: K,
    val: InstructorBlock[K]
  ) {
    onChangeInstructor({ ...instructor, [key]: val });
  }

  function updateMachine(idx: number, patch: Partial<MachineEntry>) {
    const next = equipment.machine_inventory.map((m, i) =>
      i === idx ? { ...m, ...patch } : m
    );
    onChangeEquipment({ ...equipment, machine_inventory: next });
  }

  function addMachine() {
    onChangeEquipment({
      ...equipment,
      machine_inventory: [
        ...equipment.machine_inventory,
        { type: "SMAW", count: 1, notes: "" },
      ],
    });
  }

  function removeMachine(idx: number) {
    const next = equipment.machine_inventory.filter((_, i) => i !== idx);
    onChangeEquipment({
      ...equipment,
      machine_inventory: next.length > 0 ? next : [{ type: "SMAW", count: 6, notes: "" }],
    });
  }

  const showReassurance = CROSS_TRAINED.indexOf(instructor.background_discipline) !== -1;

  return (
    <div>
      <h2>Tell us about your program</h2>
      <p className="step-description">
        We'll use this to understand what your classroom can actually support.
        None of this is shared outside forgedsight.
      </p>

      {/* ---------- Teaching background ---------- */}
      <section className="section-block">
        <h3>Your teaching background</h3>

        <label className="field">
          <span>How did you come to teach welding?</span>
          <select
            value={instructor.background_discipline}
            onChange={(e) =>
              updateInstructor("background_discipline", e.target.value as InstructorBackground)
            }
          >
            {BACKGROUND_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <small className="field-hint">
            This matters for your strategic plan. A welding teacher with years of fab
            experience and an Ag-Mech teacher assigned to welding need different plans,
            even for the same classroom. Pick what fits best — we'll tailor your plan
            accordingly.
          </small>
        </label>

        {showReassurance && (
          <div className="reassurance-banner">
            ✓ Got it. Your plan will include more week-by-week structure, more "if you
            see X do Y" rules, and a recommendation to pair with a mentor welding
            teacher. The platform is built for teachers in your situation — you're
            not alone, and your students don't need to be either.
          </div>
        )}

        {instructor.background_discipline === "other" && (
          <label className="field">
            <span>Tell us briefly so we can adapt your plan</span>
            <input
              type="text"
              maxLength={140}
              value={instructor.background_other}
              onChange={(e) => updateInstructor("background_other", e.target.value)}
            />
          </label>
        )}

        <div className="field-row">
          <label className="field">
            <span>Years teaching welding specifically (optional)</span>
            <input
              type="number"
              min={0}
              max={60}
              value={instructor.years_teaching_welding == null ? "" : instructor.years_teaching_welding}
              onChange={(e) => {
                var v = e.target.value === "" ? null : Number(e.target.value);
                updateInstructor("years_teaching_welding", v);
              }}
            />
          </label>

          <label className="field">
            <span>Years of welding / fab industry experience BEFORE teaching (optional)</span>
            <input
              type="number"
              min={0}
              max={60}
              value={instructor.years_industry_welding == null ? "" : instructor.years_industry_welding}
              onChange={(e) => {
                var v = e.target.value === "" ? null : Number(e.target.value);
                updateInstructor("years_industry_welding", v);
              }}
            />
          </label>
        </div>
      </section>

      {/* ---------- Equipment ---------- */}
      <section className="section-block">
        <h3>Equipment</h3>

        <label className="field">
          <span>Number of booths</span>
          <input
            type="number"
            min={1}
            max={60}
            value={equipment.booth_count}
            onChange={(e) =>
              onChangeEquipment({ ...equipment, booth_count: Number(e.target.value) })
            }
          />
          <small className="field-hint">Each booth supports one student welding at a time.</small>
        </label>

        <div className="field">
          <span>Welding machines (add at least one)</span>
          <div className="machine-list">
            {equipment.machine_inventory.map((m, idx) => (
              <div key={idx} className="machine-row">
                <select
                  value={m.type}
                  onChange={(e) => updateMachine(idx, { type: e.target.value as WeldProcessType })}
                >
                  {PROCESS_OPTIONS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={m.count}
                  onChange={(e) => updateMachine(idx, { count: Number(e.target.value) })}
                />
                <input
                  type="text"
                  placeholder="Notes (optional)"
                  value={m.notes}
                  onChange={(e) => updateMachine(idx, { notes: e.target.value })}
                />
                <button
                  type="button"
                  className="link-button"
                  onClick={() => removeMachine(idx)}
                >
                  ✕ Remove
                </button>
              </div>
            ))}
          </div>
          <button type="button" className="link-button" onClick={addMachine}>
            + Add machine type
          </button>
        </div>

        <label className="field">
          <span>GTAW (TIG) capability</span>
          <div className="radio-row">
            <label className="radio-pill">
              <input
                type="radio"
                checked={equipment.gtaw_capable === true}
                onChange={() => onChangeEquipment({ ...equipment, gtaw_capable: true })}
              />
              <span>Yes — at least one machine</span>
            </label>
            <label className="radio-pill">
              <input
                type="radio"
                checked={equipment.gtaw_capable === false}
                onChange={() => onChangeEquipment({ ...equipment, gtaw_capable: false })}
              />
              <span>No</span>
            </label>
          </div>
        </label>
      </section>

      {/* ---------- Calendar ---------- */}
      <section className="section-block">
        <h3>Calendar</h3>

        <div className="field-row">
          <label className="field">
            <span>Length of each class period (minutes)</span>
            <input
              type="number"
              min={1}
              max={240}
              value={calendar.class_period_length_min}
              onChange={(e) =>
                onChangeCalendar({ ...calendar, class_period_length_min: Number(e.target.value) })
              }
            />
          </label>

          <label className="field">
            <span>Class meetings per week</span>
            <input
              type="number"
              min={1}
              max={7}
              value={calendar.meetings_per_week}
              onChange={(e) =>
                onChangeCalendar({ ...calendar, meetings_per_week: Number(e.target.value) })
              }
            />
          </label>

          <label className="field">
            <span>Total term length (weeks)</span>
            <input
              type="number"
              min={1}
              max={52}
              value={calendar.total_term_weeks}
              onChange={(e) =>
                onChangeCalendar({ ...calendar, total_term_weeks: Number(e.target.value) })
              }
            />
            <small className="field-hint">
              Standard TX academic year = 36 weeks. Adjust if your program runs on a
              different schedule.
            </small>
          </label>
        </div>
      </section>
    </div>
  );
}
